import React, { useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import { trackEvent } from '@raisin/events-tracking';
import {
  CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
  CUSTOMER_PORTAL_COMPONENT,
  CustomerPortalEvent,
} from 'eventsTracking';
import { DBSProduct, OFFER_PRODUCT_TYPES } from 'types';
import { sortBankOffers } from 'utils/bankPageUtils/bankOffers';
import { getMarketingProductType } from '../../../../../utils/getMarketingProductType';
import BankOffersListHeader from './BankOffersListHeader';
import { BankOffersListItem } from './BankOffersListItem';

export interface BankOffersListProps {
  offers: DBSProduct[];
  selectedOfferId?: string;
  promotedProductId?: string | null;
  otherOffersLink?: React.ReactNode;
  expandedOffers: Array<string>;
  setExpandedOffers?: Function;
  setIsPromotedProductOpened?: Function;
}

const BankOffersList: React.FC<BankOffersListProps> = ({
  offers,
  selectedOfferId,
  promotedProductId,
  expandedOffers,
  setExpandedOffers,
  setIsPromotedProductOpened,
}) => {
  const sortedOffers = useMemo(() => sortBankOffers(offers), [offers]);

  const selectedOffer = useMemo(
    () => offers.find((offer) => offer.id === selectedOfferId),
    [offers, selectedOfferId],
  );

  const isMainOfferDisplayed = useMemo(
    () => Boolean(promotedProductId && !selectedOfferId),
    [promotedProductId, selectedOfferId],
  );

  const hasNoticeOffers = useMemo(
    () => offers.some((offer) => getMarketingProductType(offer) === OFFER_PRODUCT_TYPES.NOTICE),
    [offers, selectedOfferId],
  );

  const depositGuaranteeColumnWidth = useMemo(() => {
    let width: { [key: string]: null | number } = { xs: null, lg: null };

    sortedOffers.forEach((offer) => {
      if (
        (offer?.deposit_taking_bank?.statutory_deposit_guarantee_scheme?.logo_url || '')
          .toLowerCase()
          .includes('riksgalden')
      ) {
        width.lg = 3.8;
        width.xs = 7;
      }
    });

    return width;
  }, [sortedOffers]);

  const trackOffersListTotalOnPageLoad = () => {
    const bankId = sortedOffers[0]?.deposit_taking_bank.id;
    const offersListTotal = String(sortedOffers?.length);

    const event: CustomerPortalEvent = {
      ...CUSTOMER_PORTAL_COMMON_EVENT_PROPERTIES,
      component: CUSTOMER_PORTAL_COMPONENT.BANK_OFFERS_LIST,
      eventName: 'offers_list_loaded',
      object: 'offers_list',
      action: 'loaded',
      customAttribute1: bankId,
      customAttribute2: offersListTotal,
    };

    trackEvent(event, true);
  };

  useEffect(() => {
    trackOffersListTotalOnPageLoad();
  }, [sortedOffers]);

  return (
    <>
      <Box display={{ xs: 'none', sm: 'block' }} paddingX={6}>
        <BankOffersListHeader hasNoticeOffers={hasNoticeOffers} />
      </Box>
      {selectedOffer ? (
        <BankOffersListItem
          offer={selectedOffer}
          forceExpand
          testId="selected-offer"
          isMainOfferDisplayed={isMainOfferDisplayed}
          expandedOffers={expandedOffers}
          setExpandedOffers={setExpandedOffers}
          showTooltip={false}
          setIsPromotedProductOpened={setIsPromotedProductOpened}
          promotedProductId={promotedProductId}
          depositGuaranteeColumnWidth={depositGuaranteeColumnWidth}
        />
      ) : (
        sortedOffers.map((offer) => (
          <BankOffersListItem
            offer={offer}
            key={offer.id}
            isMainOfferDisplayed={isMainOfferDisplayed}
            expandedOffers={expandedOffers}
            setExpandedOffers={setExpandedOffers}
            setIsPromotedProductOpened={setIsPromotedProductOpened}
            promotedProductId={promotedProductId}
            depositGuaranteeColumnWidth={depositGuaranteeColumnWidth}
          />
        ))
      )}
    </>
  );
};

export default BankOffersList;
